import type { TFunction } from "i18next";

import {
  FilterDefinition,
  FilterDefinitionType,
  SchemaRelation,
  TableTypeEnum,
} from "shared/api/schemaDefinitions/types";
import {
  buildSchemaRelationEndpoint,
  getSchemaEntryLabel,
} from "shared/schemas/utils/schema-utils";

import { AnyDateDropdownOptions } from "pages/shared/Dashboard/types";

import { getFilterSummaryFieldKey } from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

export interface DashboardFilterSchemaContext {
  entries: SchemaEntry[];
  relations: SchemaRelation[];
}

export interface DashboardFilterField {
  // Key that identifies the field in the flat filter summary — the qualified
  // accessor `FilterFlatPresentation` matches on (see getFilterSummaryFieldKey).
  key: string;
  label: string;
  type: FilterDefinitionType;
  // The (possibly nested) filter definition this field came from, used to read
  // its live selected date option.
  definitionId: string;
  defaultDateOption?: AnyDateDropdownOptions;
}

/**
 * Flattens a view's filter definitions into one entry per dashboard filter
 * dropdown. Each field's `key` is the accessor it contributes to the merged
 * filter state — bare for top-level filters, and qualified by the parent
 * relation accessor for filters nested inside an IS_INCLUDED_IN (so the same
 * child attribute on two relations, e.g. `defects.line` vs `lineExits.line`,
 * stays distinct). Entries are de-duped by key unless `dedupe` is false.
 */
export const flattenDashboardFilterFields = (
  definitions: FilterDefinition[],
  eventContext: DashboardFilterSchemaContext,
  assetContext: DashboardFilterSchemaContext,
  t: TFunction,
  { dedupe = true }: { dedupe?: boolean } = {}
): DashboardFilterField[] => {
  const fields: DashboardFilterField[] = [];
  const seen = new Set<string>();

  const add = (field: DashboardFilterField) => {
    if (!field.key || (dedupe && seen.has(field.key))) return;

    seen.add(field.key);
    fields.push(field);
  };

  definitions.forEach((definition) => {
    const context =
      definition.schemaType === TableTypeEnum.EVENT
        ? eventContext
        : assetContext;

    switch (definition.type) {
      case FilterDefinitionType.DATE:
        if (definition.dateField) {
          add({
            key: getFilterSummaryFieldKey(definition.dateField),
            label: getSchemaEntryLabel(context.entries, definition.dateField),
            type: FilterDefinitionType.DATE,
            definitionId: definition.id,
            defaultDateOption:
              definition.defaultDateOption ??
              definition.dateFieldDropdownOptions?.[0],
          });
        }

        break;
      case FilterDefinitionType.QUICK_FILTER:
        (definition.defaultFilter ?? []).forEach((row) => {
          if (row.attribute) {
            add({
              key: getFilterSummaryFieldKey(row.attribute),
              label: getSchemaEntryLabel(context.entries, row.attribute),
              type: FilterDefinitionType.QUICK_FILTER,
              definitionId: definition.id,
            });
          }
        });
        break;
      case FilterDefinitionType.KEYWORD_SEARCH:
        add({
          key: getFilterSummaryFieldKey("search"),
          label: t("filters:keyword_search"),
          type: FilterDefinitionType.KEYWORD_SEARCH,
          definitionId: definition.id,
        });
        break;
      case FilterDefinitionType.IS_INCLUDED_IN: {
        const relation = context.relations.find(
          (r) => r.relatedSchemaID === definition.relatedSchemaID
        );
        if (!relation?.relatedSchema) break;

        const endpoint = buildSchemaRelationEndpoint(
          relation.relatedSchema.tableType,
          relation.relatedSchema.name
        );
        const parentEntry = context.entries.find(
          (e) => e.filter?.relationEndpoint === endpoint
        );
        if (!parentEntry) break;

        const nestedEntries = parentEntry.relationSchema ?? [];
        const relationLabel =
          relation.relatedSchema.displayNamePlural ??
          relation.relatedSchema.name ??
          relation.key;
        const prefix = parentEntry.accessor;

        const addNested = (
          attribute: string,
          type: FilterDefinitionType,
          definitionId: string,
          defaultDateOption?: AnyDateDropdownOptions
        ) =>
          add({
            key: getFilterSummaryFieldKey(attribute, prefix),
            label: `${relationLabel} - ${getSchemaEntryLabel(nestedEntries, attribute)}`,
            type,
            definitionId,
            defaultDateOption,
          });

        (definition.nestedFilterDefinitions ?? []).forEach((nested) => {
          if (nested.type === FilterDefinitionType.DATE) {
            if (nested.dateField) {
              addNested(
                nested.dateField,
                FilterDefinitionType.DATE,
                nested.id,
                nested.defaultDateOption ?? nested.dateFieldDropdownOptions?.[0]
              );
            }
          } else if (nested.type === FilterDefinitionType.QUICK_FILTER) {
            (nested.defaultFilter ?? []).forEach((row) => {
              if (row.attribute) {
                addNested(
                  row.attribute,
                  FilterDefinitionType.QUICK_FILTER,
                  nested.id
                );
              }
            });
          }
        });
        break;
      }
    }
  });

  return fields;
};
